import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { membersAPI } from '../services/api';
import MemberForm from './MemberForm';

const MemberList = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvUploadError, setCsvUploadError] = useState('');
  const [csvResult, setCsvResult] = useState(null);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // Reset to first page on new search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await membersAPI.getAll(page, search);
      setMembers(data.results || data);
      setTotalPages(Math.ceil((data.count || data.length || 0) / 10));
      setCount(data.count || data.length || 0);
    } catch (err) {
      setError('Failed to fetch members. Please try again.');
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    // Fetch whenever `page` or `search` changes (debounced via `searchInput` effect).
    fetchMembers();
  }, [fetchMembers]);

  const handleCreate = () => {
    setEditingMember(null);
    setOpenForm(true);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setOpenForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this member?')) {
      try {
        await membersAPI.delete(id);
        fetchMembers();
      } catch (err) {
        alert('Failed to delete member. Please try again.');
        console.error('Error deleting member:', err);
      }
    }
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setEditingMember(null);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    fetchMembers();
  };

  const csvEscape = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    // Escape double quotes by doubling them.
    const escaped = str.replace(/"/g, '""');
    // Quote if it contains CSV-special characters.
    if (/[",\n]/.test(str)) return `"${escaped}"`;
    return escaped;
  };

  const handleDownloadSampleCsv = () => {
    const headers = [
      'KOALM_number',
      'name',
      'email',
      'IOA_LM_number',
      'working_hospital_name',
      'working_hospital_district',
      'designation',
      'communication_address',
      'address1_place_post',
      'district',
      'pincode',
      'state',
      'district_club_name',
      'mobile_number',
      'whatsapp_number',
      'landline_hospital',
      'landline_residence',
      'date_of_birth',
      'blood_group',
      'tcmc_reg_no',
      'ima_life_membership_status',
      'mbbs_college',
      'mbbs_year',
      'pg_diploma_college',
      'pg_diploma_year',
      'pg_degree_college',
      'pg_degree_year',
      'awards_honours',
      'spouse_name',
      // Optional
      'address',
    ];

    const sampleRow = {
      KOALM_number: 'KOA-001',
      name: 'John Doe',
      email: 'johndoe@example.com',
      IOA_LM_number: '',
      working_hospital_name: 'Example Hospital',
      working_hospital_district: 'Thiruvananthapuram',
      designation: 'Surgeon',
      communication_address: 'House No 1, Street Name',
      address1_place_post: 'Palayam, Thiruvananthapuram',
      district: 'Thiruvananthapuram',
      pincode: '695001',
      state: 'Kerala',
      district_club_name: 'K O A Club',
      mobile_number: '9999999999',
      whatsapp_number: '',
      landline_hospital: '',
      landline_residence: '',
      date_of_birth: '1980-01-01',
      blood_group: 'O+',
      tcmc_reg_no: '',
      ima_life_membership_status: 'Yes',
      mbbs_college: 'Example Medical College',
      mbbs_year: '2002',
      pg_diploma_college: '',
      pg_diploma_year: '',
      pg_degree_college: '',
      pg_degree_year: '',
      awards_honours: '',
      spouse_name: '',
      address: '',
    };

    const csv =
      `${headers.join(',')}\n` +
      `${headers.map((h) => csvEscape(sampleRow[h])).join(',')}\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'members_sample.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCsvOpen = () => {
    setCsvUploadOpen(true);
    setCsvFile(null);
    setCsvUploading(false);
    setCsvUploadError('');
    setCsvResult(null);
  };

  const handleCsvClose = () => {
    setCsvUploadOpen(false);
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    setCsvFile(file || null);
    setCsvUploadError('');
    setCsvResult(null);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      setCsvUploadError('Please select a CSV file first.');
      return;
    }

    setCsvUploading(true);
    setCsvUploadError('');
    setCsvResult(null);

    try {
      const res = await membersAPI.importCSV(csvFile);
      setCsvResult(res);
      fetchMembers();

      if (res?.errors && res.errors.length > 0) {
        // Keep dialog open so admin can see row-level errors.
        return;
      }

      setCsvUploadOpen(false);
    } catch (err) {
      setCsvUploadError('Failed to import CSV. Please try again.');
      console.error('Error importing CSV:', err);
    } finally {
      setCsvUploading(false);
    }
  };

  if (loading && members.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight={600}>
          Members
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={handleDownloadSampleCsv}
            size="large"
          >
            Download Sample CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={handleCsvOpen}
            size="large"
          >
            Upload CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            size="large"
          >
            Add Member
          </Button>
        </Stack>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          placeholder="Search members..."
          variant="outlined"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <Typography variant="body2" color="text.secondary">
          {count} member{count !== 1 ? 's' : ''} found
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>KOALM Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Mobile</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Designation</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>District</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>District Club</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No members found. Click "Add Member" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              members.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell>{member.name || '-'}</TableCell>
                  <TableCell>{member.email || '-'}</TableCell>
                  <TableCell>
                    {member.KOALM_number ? (
                      <Chip label={member.KOALM_number} size="small" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{member.mobile_number || '-'}</TableCell>
                  <TableCell>{member.designation || '-'}</TableCell>
                  <TableCell>{member.district || '-'}</TableCell>
                  <TableCell>{member.district_club_name || '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(member)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(member.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <MemberForm
        open={openForm}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        member={editingMember}
      />

      <Dialog open={csvUploadOpen} onClose={handleCsvClose} maxWidth="md" fullWidth>
        <DialogTitle>Upload Members CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a CSV with headers matching the sample. Import uses <b>KOALM_number</b> to
              create/update records.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Button variant="outlined" component="label" disabled={csvUploading}>
                Choose CSV
                <input
                  hidden
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvFileChange}
                />
              </Button>

              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                {csvFile ? `Selected: ${csvFile.name}` : 'No file selected'}
              </Typography>
            </Box>

            {csvUploadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {csvUploadError}
              </Alert>
            )}

            {csvResult && (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Import finished. Created: {csvResult.created || 0}, Updated: {csvResult.updated || 0}
                </Alert>

                {csvResult.errors && csvResult.errors.length > 0 && (
                  <Alert severity="warning">
                    Some rows had errors. Showing first {Math.min(5, csvResult.errors.length)}:
                    <Typography component="div" variant="body2" sx={{ mt: 1 }}>
                      {csvResult.errors.slice(0, 5).map((e, idx) => (
                        <div key={idx}>
                          Row {e.row}: {e.error}
                        </div>
                      ))}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCsvClose} disabled={csvUploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCsvUpload}
            disabled={csvUploading || !csvFile}
          >
            {csvUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemberList;

